import {
  ALL_BLOCKED, IAccessRulesBlock, IBlockActionReason, IPropertyRule, IWriteBlocker, TWriteBlockedAction,
} from './types';

/**
 * Pure guards over an @accessRules block.
 *
 * Tolerance principle (backward compatibility): a missing block or a missing
 * per-field rule means "no restrictions" — legacy payloads keep working exactly
 * as before the mechanic existed.
 */

export const getRule = (
  block: IAccessRulesBlock | undefined | null,
  field: string,
): IPropertyRule | undefined => {
  if (!block || !Array.isArray(block.properties)) return undefined;
  return block.properties.find((p) => p && p.name === field);
};

export const canRead = (
  block: IAccessRulesBlock | undefined | null,
  field: string,
): boolean => {
  const rule = getRule(block, field);
  if (!rule) return true;
  return rule.read !== false;
};

/** All read-deny reasons of the field (empty when readable or degraded). */
export const readBlockReasons = (
  block: IAccessRulesBlock | undefined | null,
  field: string,
): IBlockActionReason[] => {
  const rule = getRule(block, field);
  if (!rule || rule.read !== false || !Array.isArray(rule.readBlockReasons)) return [];
  return rule.readBlockReasons.filter((r) => !!r);
};

/** First human-readable read-deny message, if any. */
export const readBlockReason = (
  block: IAccessRulesBlock | undefined | null,
  field: string,
): string | null => {
  return readBlockReasons(block, field).find((r) => r.message != null)?.message ?? null;
};

const findBlocker = (
  rule: IPropertyRule | undefined,
  action: TWriteBlockedAction,
): IWriteBlocker | undefined => {
  if (!rule || !Array.isArray(rule.writeBlockers)) return undefined;
  return rule.writeBlockers.find(
    (b) => b && (b.blockActionName === ALL_BLOCKED || b.blockActionName === action),
  );
};

export const canWrite = (
  block: IAccessRulesBlock | undefined | null,
  field: string,
  action: TWriteBlockedAction,
): boolean => {
  return !findBlocker(getRule(block, field), action);
};

/**
 * All reasons of the blocker matching the action (empty when the write is
 * allowed or the payload is degraded). `type` is machine-readable, `message`
 * is the backend's human-readable text.
 */
export const writeBlockReasons = (
  block: IAccessRulesBlock | undefined | null,
  field: string,
  action: TWriteBlockedAction,
): IBlockActionReason[] => {
  const blocker = findBlocker(getRule(block, field), action);
  if (!blocker || !Array.isArray(blocker.blockActionReasons)) return [];
  return blocker.blockActionReasons.filter((r) => !!r);
};

/** First human-readable reason message, if any. */
export const writeBlockReason = (
  block: IAccessRulesBlock | undefined | null,
  field: string,
  action: TWriteBlockedAction,
): string | null => {
  const reasons = writeBlockReasons(block, field, action);
  return reasons.find((r) => r.message != null)?.message ?? null;
};

export const isFullyBlocked = (
  block: IAccessRulesBlock | undefined | null,
  field: string,
): boolean => {
  const rule = getRule(block, field);
  if (!rule || !Array.isArray(rule.writeBlockers)) return false;
  return rule.writeBlockers.some((b) => b && b.blockActionName === ALL_BLOCKED);
};

export const uiHint = (
  block: IAccessRulesBlock | undefined | null,
  field: string,
): string | null => {
  const rule = getRule(block, field);
  return rule ? (rule.ui ?? null) : null;
};

/**
 * Backend emits an entry ONLY for restricted fields, and read-denied values are
 * removed from the payload. So: value absent + entry present = "restricted",
 * value absent + no entry = genuinely no data.
 */
export const isRestricted = (
  block: IAccessRulesBlock | undefined | null,
  field: string,
): boolean => {
  return !!getRule(block, field);
};
